import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, Upload, Download, Trash2, FileText, Save, CalendarCheck2, X as XIcon, Check, ArrowLeftCircle, ArrowRightCircle, FileSpreadsheet, EyeOff, Sparkles, FileCheck, Wand2, Eye, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useModal } from '../components/AppModal';
import useUserPreference from '../hooks/useUserPreference';
import useTrimestreDeTrabajo from '../hooks/useTrimestreDeTrabajo';
import useViewMode from '../hooks/useViewMode';
import useFieldLayout from '../hooks/useFieldLayout';
import VistaControles from '../components/VistaControles';
import AccionesSeleccion from '../components/AccionesSeleccion';
import CierreTrimestreModal from '../components/CierreTrimestreModal';
import DataTable from '../components/DataTable';
import DecimalInput from '../components/DecimalInput';

const ReceivedInvoices = () => {
  const { hasPermission, activeTenant } = useAuth();
  const modal = useModal();
  const fileInputRef = useRef(null);

  const [items, setItems] = useState([]);
  const [quarterClosures, setQuarterClosures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [reextracting, setReextracting] = useState(false);
  const [reextractingId, setReextractingId] = useState(null);
  const [regeneratingId, setRegeneratingId] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  // Cierre de trimestre en curso (modal «Cerrar trimestre»): una línea por
  // trimestre en el que están las facturas que se cierran. null = modal cerrado.
  const [cierre, setCierre] = useState(null);
  const today = new Date();
  const currentQuarter = Math.floor(today.getMonth() / 3) + 1;
  const currentYear = today.getFullYear();
  const [filters, setFilters] = useUserPreference(`received-invoices:t${activeTenant?.id ?? 'default'}`, {
    search: '',
    visibleFilter: '',
    closedFilter: '',
    accountedFilter: '',
    supplierFilter: '',
    sortKey: 'date_desc',
  });
  const search = filters.search ?? '';
  const visibleFilter = filters.visibleFilter ?? '';
  const closedFilter = filters.closedFilter ?? '';
  const accountedFilter = filters.accountedFilter ?? '';
  const supplierFilter = filters.supplierFilter ?? '';
  const sortKey = filters.sortKey ?? 'date_desc';
  const { year, quarter, setYear, setQuarter } = useTrimestreDeTrabajo('entrantes');
  const [viewMode, setViewMode] = useViewMode('received-invoices');
  const fl = useFieldLayout('received-invoices', viewMode);

  // Trimestre en que se cerró una factura: el anterior al de la fecha de
  // cierre, porque el trimestre se cierra cuando la gestoría avisa de que ya
  // no admite más, y eso pasa ya entrado el siguiente.
  const quarterOfClosing = (closedAt) => {
    const c = new Date(closedAt);
    if (isNaN(c.getTime())) return null;
    const cy = c.getFullYear();
    const cq = Math.floor(c.getMonth() / 3) + 1;
    return cq > 1 ? { year: cy, quarter: cq - 1 } : { year: cy - 1, quarter: 4 };
  };

  // Trimestres ya cerrados: los del registro de cierres y, además, los que se
  // deducen de las facturas que llevan la marca (cierres anteriores a que
  // existiera ese registro).
  const closedQuarters = useMemo(() => {
    const cerrados = new Set();
    for (const cierre of quarterClosures) {
      if (cierre?.year && cierre?.quarter) cerrados.add(`${cierre.year}-${cierre.quarter}`);
    }
    for (const item of items) {
      if (!item?.quarterClosedAt) continue;
      // Una factura cerrada con el trimestre fijado (el que se eligió al
      // cerrarla) cierra ESE trimestre, no el que saldría del día del cierre.
      const q = (item.quarterOverrideYear && item.quarterOverrideQuarter)
        ? { year: item.quarterOverrideYear, quarter: item.quarterOverrideQuarter }
        : quarterOfClosing(item.quarterClosedAt);
      if (q) cerrados.add(`${q.year}-${q.quarter}`);
    }
    return cerrados;
  }, [items, quarterClosures]);

  // Trimestre lógico (efectivo):
  //  1. Si hay override manual → ese trimestre.
  //  2. Si quarterClosedAt no nulo → el trimestre con el que se cerró. Una
  //     factura cerrada NUNCA cambia de trimestre (norma del usuario,
  //     2026-09-01): ahí es donde se declaró a la gestoría.
  //  3. Resto → el trimestre de su FECHA, salvo que ese ya esté cerrado; en
  //     ese caso la factura llegó tarde y va al primero que siga abierto, que
  //     es el envío en el que entrará. Colocarla por su fecha la escondería
  //     entre las ya declaradas y se quedaría sin enviar.
  const getReceivedQuarter = (item) => {
    if (item?.quarterOverrideYear && item?.quarterOverrideQuarter) {
      return { year: item.quarterOverrideYear, quarter: item.quarterOverrideQuarter };
    }
    if (item?.quarterClosedAt) {
      const q = quarterOfClosing(item.quarterClosedAt);
      if (q) return q;
    }
    const raw = item?.date || item?.createdAt;
    if (!raw) return null;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return null;
    let anio = d.getFullYear();
    let trimestre = Math.floor(d.getMonth() / 3) + 1;
    // Como mucho ocho saltos: con más, algo va mal en los datos y se deja
    // donde estaba en vez de girar sin fin.
    for (let saltos = 0; saltos < 8 && closedQuarters.has(`${anio}-${trimestre}`); saltos++) {
      trimestre += 1;
      if (trimestre > 4) { trimestre = 1; anio += 1; }
    }
    return { year: anio, quarter: trimestre };
  };

  useEffect(() => { loadItems(); }, []);

  const loadItems = async () => {
    try {
      const res = await api.get('/received-invoices');
      setItems(res.data);
      const validIds = new Set(res.data.map(item => item.id));
      setSelectedIds(prev => prev.filter(id => validIds.has(id)));
    } catch {
      modal.error('Error cargando facturas recibidas');
    } finally {
      setLoading(false);
    }
    // Los trimestres cerrados van aparte: si fallan, el listado se ve igual y
    // los cierres se siguen deduciendo de las facturas marcadas.
    try {
      const cierres = await api.get('/received-invoices/quarter-closures');
      setQuarterClosures(Array.isArray(cierres.data) ? cierres.data : []);
    } catch {
      setQuarterClosures([]);
    }
  };

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    setUploading(true);

    const uploaded = [];
    const errors = [];
    const skipped = [];

    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setUploadProgress({ current: i + 1, total: files.length, fileName: f.name });
      try {
        const fd = new FormData();
        fd.append('files', f);
        const res = await api.post('/received-invoices/upload', fd);
        if (res.data[0]?.success) {
          const uploadedItem = res.data[0].item;
          uploaded.push(uploadedItem);
          setItems(prev => [uploadedItem, ...prev]);
        } else if (res.data[0]?.duplicate) {
          skipped.push(res.data[0]?.fileName || f.name);
        } else {
          // Con el motivo que da el servidor (p. ej., un PDF que no se ha
          // podido leer y no se ha guardado).
          const motivo = res.data[0]?.error;
          errors.push(`${res.data[0]?.fileName || f.name}${motivo ? ` (${motivo})` : ''}`);
        }
      } catch {
        errors.push(f.name);
      }
    }

    if (uploaded.length > 0) {
      const extras = [];
      if (skipped.length > 0) extras.push(`${skipped.length} ya existente${skipped.length > 1 ? 's' : ''} omitida${skipped.length > 1 ? 's' : ''}`);
      const suffix = extras.length > 0 ? ` · ${extras.join(' · ')}` : '';
      modal.success(`${uploaded.length} archivo${uploaded.length > 1 ? 's' : ''} subido${uploaded.length > 1 ? 's' : ''}${suffix}`);
    } else if (skipped.length > 0 && errors.length === 0) {
      modal.info(`${skipped.length} factura${skipped.length > 1 ? 's' : ''} ya existente${skipped.length > 1 ? 's' : ''}, no se ${skipped.length > 1 ? 'han' : 'ha'} vuelto a añadir.`);
    }
    if (errors.length > 0) {
      modal.error(`Error en: ${errors.join(', ')}`);
    }

    setUploading(false);
    setUploadProgress(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = (item) => {
    modal.confirm(
      `¿Eliminar "${item.fileName}"?`,
      () => {
        api.delete(`/received-invoices/${item.id}`)
          .then(async () => {
            await loadItems();
          })
          .catch(() => modal.error('Error eliminando'));
      }
    );
  };

  const handleDownload = async (item) => {
    try {
      const res = await api.get(`/received-invoices/${item.id}/file`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = item.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      modal.error('Error descargando');
    }
  };

  // Ver el documento original en una pestaña, sin descargarlo (como «Ver PDF»
  // de Salientes). El blob conserva el tipo (PDF o imagen) que envía el servidor.
  const handleViewFile = async (item) => {
    try {
      const res = await api.get(`/received-invoices/${item.id}/file`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch {
      modal.error('Error abriendo el documento');
    }
  };

  const handleReextract = () => {
    if (selectedIds.length === 0) {
      modal.error('Marca al menos una factura con el checkbox para reprocesar.');
      return;
    }
    modal.confirm(
      `¿Re-extraer los datos OCR de ${selectedIds.length} factura${selectedIds.length === 1 ? '' : 's'} marcada${selectedIds.length === 1 ? '' : 's'}? Sólo se sobrescriben los campos detectados; los proveedores nuevos se dan de alta automáticamente por NIF.`,
      async () => {
        setReextracting(true);
        try {
          const res = await api.post('/received-invoices/reextract', { ids: selectedIds });
          await loadItems();
          modal.success(res.data?.message || 'Re-extracción completada.');
        } catch (error) {
          modal.error(error.response?.data?.message || 'Error reprocesando facturas');
        } finally {
          setReextracting(false);
        }
      }
    );
  };

  const handleReextractOne = async (item) => {
    if (reextractingId) return;
    setReextractingId(item.id);
    try {
      const res = await api.post('/received-invoices/reextract', { ids: [item.id] });
      await loadItems();
      modal.success(res.data?.message || 'Re-extracción completada.');
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error re-extrayendo datos');
    } finally {
      setReextractingId(null);
    }
  };

  // Marca que la plantilla con la que se leyó esta factura no vale: se retira,
  // la IA describe otra vez la disposición de los campos a partir de este
  // documento y la factura se relee con la plantilla nueva.
  const handleRegenerateTemplate = async (item) => {
    if (regeneratingId) return;
    setRegeneratingId(item.id);
    try {
      const res = await api.post(`/received-invoices/${item.id}/regenerate-template`);
      await loadItems();
      modal.success(res.data?.message || 'Plantilla regenerada.');
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error regenerando la plantilla');
    } finally {
      setRegeneratingId(null);
    }
  };

  const handleToggleVisibleAsesoria = async (item) => {
    const next = !item.visibleAsesoria;
    try {
      const res = await api.patch(`/received-invoices/${item.id}/visible-asesoria`, { visible: next });
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, visibleAsesoria: res.data?.visibleAsesoria ?? next } : i));
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error actualizando visibilidad asesoría');
    }
  };

  const bulkApply = (label, ids, fn) => {
    if (ids.length === 0) return;
    modal.confirm(
      `¿${label} en ${ids.length} factura${ids.length === 1 ? '' : 's'} seleccionada${ids.length === 1 ? '' : 's'}?`,
      async () => {
        setBulkRunning(true);
        try {
          const res = await fn(ids);
          await loadItems();
          setSelectedIds([]);
          modal.success(res?.data?.message || `${label}: aplicado a ${ids.length} factura${ids.length === 1 ? '' : 's'}.`);
        } catch (error) {
          modal.error(error.response?.data?.message || `Error en operación: ${label}`);
        } finally {
          setBulkRunning(false);
        }
      }
    );
  };

  // Cerrar el trimestre visible entero (todas sus facturas sin cerrar), como el
  // botón «Cerrar T» de Salientes. El cierre de recibidas no se puede revertir.
  const closeCurrentQuarter = () => {
    if (quarter === 'all') return;
    const ids = items.filter(item => {
      const qi = getReceivedQuarter(item);
      return qi && qi.year === year && qi.quarter === quarter && !item.quarterClosedAt;
    }).map(item => item.id);
    if (ids.length === 0) return;
    setCierre({ grupos: [{ year, quarter, ids, cuantas: ids.length }] });
  };

  // Aceptado el modal «Cerrar trimestre»: cada grupo se cierra con el trimestre
  // elegido para él, que es además el que queda apuntado como cerrado.
  const confirmarCierre = async (destinos) => {
    if (!cierre) return;
    setBulkRunning(true);
    try {
      const partes = [];
      for (let i = 0; i < cierre.grupos.length; i += 1) {
        const g = cierre.grupos[i];
        const d = destinos[i];
        const res = await api.post('/received-invoices/quarter-close', { receivedInvoiceIds: g.ids, year: d.year, quarter: d.quarter });
        const hechas = res.data.count;
        partes.push(`${hechas} factura${hechas === 1 ? '' : 's'} en el T${d.quarter} de ${d.year}`);
      }
      setCierre(null);
      await loadItems();
      setSelectedIds([]);
      modal.success(`Trimestre cerrado: ${partes.join('; ')}.`);
    } catch (error) {
      setCierre(null);
      await loadItems();
      modal.error(error.response?.data?.message || 'Error cerrando el trimestre');
    } finally {
      setBulkRunning(false);
    }
  };

  // Las acciones en lote SOLO operan sobre la selección del TRIMESTRE actual:
  // `selectedIds` no se limpia al navegar, así que se restringe a `trimestreIdSet`
  // para no actuar sobre selecciones "fantasma" de otros trimestres, pero SÍ se
  // incluyen las del trimestre que un sub-filtro oculte (las que marca "Marcar
  // todas del trimestre"). Los contadores de los botones usan el mismo criterio.
  const idsSeleccionadosDelTrimestre = (pred) =>
    items.filter(it => selectedSet.has(it.id) && trimestreIdSet.has(it.id) && pred(it)).map(it => it.id);

  const bulkVisibleAsesoria = (visible) => {
    const ids = idsSeleccionadosDelTrimestre(it => Boolean(it.visibleAsesoria) !== visible);
    bulkApply(
      visible ? 'Marcar como visibles para la asesoría' : 'Ocultar a la asesoría',
      ids,
      (idsArr) => api.post('/received-invoices/bulk-visible-asesoria', { ids: idsArr, visible })
    );
  };

  // Contabilizada: se marca cuando la asesoría confirma que las ha contabilizado.
  // Se refleja en el portal de asesoría (estado "contabilizada").
  const bulkMarkAccounted = (value) => {
    const ids = idsSeleccionadosDelTrimestre(it => Boolean(it.isAccounted) !== value);
    bulkApply(
      value ? 'Marcar como contabilizadas' : 'Quitar la marca de contabilizada',
      ids,
      (idsArr) => api.post('/received-invoices/bulk-accounted', { ids: idsArr, value })
    );
  };

  const toggleAccounted = async (id, value) => {
    try {
      await api.post('/received-invoices/bulk-accounted', { ids: [id], value });
      setItems(items.map(i => i.id === id ? { ...i, isAccounted: value } : i));
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error actualizando contabilizada');
    }
  };

  // Cerrar las marcadas: abre el modal «Cerrar trimestre» con una línea por
  // trimestre en el que están (desde la vista «Año» puede haber varios).
  const bulkQuarterClose = () => {
    const porTrimestre = new Map();
    items.filter(it => selectedSet.has(it.id) && trimestreIdSet.has(it.id) && !it.quarterClosedAt).forEach(it => {
      const q = getReceivedQuarter(it);
      if (!q) return;
      const clave = `${q.year}-${q.quarter}`;
      if (!porTrimestre.has(clave)) porTrimestre.set(clave, { year: q.year, quarter: q.quarter, ids: [] });
      porTrimestre.get(clave).ids.push(it.id);
    });
    const grupos = [...porTrimestre.values()]
      .sort((a, b) => (a.year - b.year) || (a.quarter - b.quarter))
      .map(g => ({ ...g, cuantas: g.ids.length }));
    if (grupos.length === 0) return;
    setCierre({ grupos });
  };

  const bulkQuarterShift = (direction) => {
    if (!['prev', 'next', 'clear'].includes(direction)) return;
    const ids = idsSeleccionadosDelTrimestre(() => true);
    const label = direction === 'prev'
      ? 'Mover al trimestre anterior'
      : direction === 'next'
        ? 'Mover al trimestre siguiente'
        : 'Quitar el cambio de trimestre';
    bulkApply(
      label,
      ids,
      (idsArr) => api.post('/received-invoices/bulk-quarter-override', { ids: idsArr, direction })
    );
  };

  const openDetail = (item) => {
    setDetailItem(item);
    setEditForm({
      providerName: item.providerName || '',
      providerNif: item.providerNif || '',
      invoiceNumber: item.invoiceNumber || '',
      date: item.date ? new Date(item.date).toISOString().split('T')[0] : '',
      amount: item.amount || '',
      taxAmount: item.taxAmount || '',
      notes: item.notes || ''
    });
  };

  const closeDetail = () => {
    setDetailItem(null);
    setEditForm(null);
  };

  const handleSaveDetail = async () => {
    if (!detailItem) return;
    setSaving(true);
    try {
      const res = await api.put(`/received-invoices/${detailItem.id}`, editForm);
      await loadItems();
      setDetailItem(res.data);
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error guardando');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

  const formatDateTime = (d) =>
    d
      ? new Date(d).toLocaleString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      : '—';

  const formatCurrency = (v) =>
    v ? new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(v) : '—';

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const filtered = items.filter(item => {
    const matchSearch = !search ||
      item.fileName.toLowerCase().includes(search.toLowerCase()) ||
      (item.providerName || '').toLowerCase().includes(search.toLowerCase()) ||
      (item.providerNif || '').toLowerCase().includes(search.toLowerCase()) ||
      (item.invoiceNumber || '').toLowerCase().includes(search.toLowerCase());
    const matchVisible = !visibleFilter
      || (visibleFilter === 'si' && !!item.visibleAsesoria)
      || (visibleFilter === 'no' && !item.visibleAsesoria);
    const matchClosed = !closedFilter
      || (closedFilter === 'si' && !!item.quarterClosedAt)
      || (closedFilter === 'no' && !item.quarterClosedAt);
    const matchAccounted = !accountedFilter
      || (accountedFilter === 'si' && !!item.isAccounted)
      || (accountedFilter === 'no' && !item.isAccounted);
    const matchSupplier = !supplierFilter || (item.providerName || '') === supplierFilter;
    const q = getReceivedQuarter(item);
    const matchQuarter = q && q.year === year && (quarter === 'all' || q.quarter === quarter);
    return matchSearch && matchVisible && matchClosed && matchAccounted && matchSupplier && matchQuarter;
  });

  const sortedFiltered = (() => {
    const arr = [...filtered];
    const dt = (it) => new Date(it.date || it.createdAt).getTime() || 0;
    const cmp = (a, b) => {
      switch (sortKey) {
        case 'date_asc': return dt(a) - dt(b);
        case 'date_desc': return dt(b) - dt(a);
        case 'provider_asc': return (a.providerName || '').localeCompare(b.providerName || '', 'es');
        case 'provider_desc': return (b.providerName || '').localeCompare(a.providerName || '', 'es');
        case 'amount_asc': return (parseFloat(a.amount) || 0) - (parseFloat(b.amount) || 0);
        case 'amount_desc': return (parseFloat(b.amount) || 0) - (parseFloat(a.amount) || 0);
        case 'invoice_num': return (a.invoiceNumber || '').localeCompare(b.invoiceNumber || '', 'es', { numeric: true });
        case 'invoice_num_desc': return (b.invoiceNumber || '').localeCompare(a.invoiceNumber || '', 'es', { numeric: true });
        default: return 0;
      }
    };
    return arr.sort(cmp);
  })();

  const activeFilterCount = [
    search ? 1 : 0,
    visibleFilter ? 1 : 0,
    closedFilter ? 1 : 0,
    accountedFilter ? 1 : 0,
    supplierFilter ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const exportCsv = () => {
    const header = ['Archivo', 'Fecha', 'Proveedor', 'NIF', 'N.º factura', 'Base imponible', 'IVA', 'Total', 'Visible asesoría', 'Trimestre cerrado'];
    const num = (n) => (parseFloat(n) || 0).toFixed(2).replace('.', ',');
    const rows = sortedFiltered.map(item => {
      const total = parseFloat(item.amount) || 0;
      const iva = parseFloat(item.taxAmount) || 0;
      const base = total - iva;
      return [
        (item.fileName || '').replace(/"/g, '""'),
        (item.date || '').slice(0, 10),
        (item.providerName || '').replace(/"/g, '""'),
        (item.providerNif || '').replace(/"/g, '""'),
        (item.invoiceNumber || '').replace(/"/g, '""'),
        num(base),
        num(iva),
        num(total),
        item.visibleAsesoria ? 'sí' : 'no',
        item.quarterClosedAt ? 'sí' : 'no',
      ];
    });
    const csv = '﻿' + [header, ...rows].map(r => r.map(c => `"${c}"`).join(';')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `facturas_recibidas_${year}_${quarter === 'all' ? 'anual' : 'T' + quarter}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const totals = filtered.reduce((acc, item) => {
    const total = parseFloat(item.amount) || 0;
    const iva = parseFloat(item.taxAmount) || 0;
    acc.iva += iva;
    acc.base += (total - iva);
    acc.total += total;
    return acc;
  }, { base: 0, iva: 0, total: 0 });

  const quarterCounts = [1, 2, 3, 4].map(q => items.filter(item => {
    const qi = getReceivedQuarter(item);
    return qi && qi.year === year && qi.quarter === q;
  }).length);

  // Facturas del trimestre visible aún sin cerrar (para el botón «Cerrar T»).
  const closableCount = quarter === 'all' ? 0 : items.filter(item => {
    const qi = getReceivedQuarter(item);
    return qi && qi.year === year && qi.quarter === quarter && !item.quarterClosedAt;
  }).length;

  const availableYears = Array.from(new Set(
    items.map(item => getReceivedQuarter(item)?.year).filter(Boolean)
  )).sort((a, b) => a - b);
  const minYear = availableYears.length > 0 ? Math.min(...availableYears, currentYear) : currentYear;
  const maxYear = availableYears.length > 0 ? Math.max(...availableYears, currentYear) : currentYear;

  const selectedSet = new Set(selectedIds);
  const selectableFilteredIds = sortedFiltered.map(item => item.id);
  // Ids del TRIMESTRE actual, por el mismo criterio que las pestañas
  // (getReceivedQuarter), SIN aplicar los sub-filtros (búsqueda, visible,
  // cerrado, contabilizada, duplicada, proveedor). Es lo que selecciona
  // "Marcar todas del trimestre": todas las de la fecha del trimestre,
  // aparezcan o no en el filtro.
  const idsDelTrimestre = items
    .filter(item => {
      const q = getReceivedQuarter(item);
      return q && q.year === year && (quarter === 'all' || q.quarter === quarter);
    })
    .map(item => item.id);
  const trimestreIdSet = new Set(idsDelTrimestre);
  const allTrimestreSelected = idsDelTrimestre.length > 0 && idsDelTrimestre.every(id => selectedSet.has(id));
  // Las acciones y sus contadores operan SOLO sobre la selección del TRIMESTRE
  // actual (no sobre `items` global ni sobre otros trimestres): así una selección
  // de otro trimestre no dispara los botones (la selección no se limpia al
  // navegar) y, a la vez, se pueden marcar facturas del trimestre que un
  // sub-filtro oculte.
  const shownSelected = items.filter(item => selectedSet.has(item.id) && trimestreIdSet.has(item.id));
  const selectedNotClosedCount = shownSelected.filter(item => !item.quarterClosedAt).length;
  const selectedNotVisibleCount = shownSelected.filter(item => !item.visibleAsesoria).length;
  const selectedVisibleCount = shownSelected.filter(item => item.visibleAsesoria).length;
  const selectedNotAccountedCount = shownSelected.filter(item => !item.isAccounted).length;
  const selectedAccountedCount = shownSelected.filter(item => item.isAccounted).length;
  const selectedShownCount = shownSelected.length;
  const allFilteredSelected = selectableFilteredIds.length > 0
    && selectableFilteredIds.every(id => selectedSet.has(id));

  const toggleSelectAllFiltered = () => {
    if (selectableFilteredIds.length === 0) return;
    setSelectedIds(prev => {
      const prevSet = new Set(prev);
      const areAllSelected = selectableFilteredIds.every(id => prevSet.has(id));
      if (areAllSelected) {
        return prev.filter(id => !selectableFilteredIds.includes(id));
      }
      return [...new Set([...prev, ...selectableFilteredIds])];
    });
  };

  // Selecciona/deselecciona TODAS las facturas del trimestre actual (por su
  // fecha/trimestre), aparezcan o no en el filtro visible. Solo selecciona; no
  // cambia ningún estado.
  const toggleSelectAllTrimestre = () => {
    if (idsDelTrimestre.length === 0) return;
    setSelectedIds(prev => {
      const prevSet = new Set(prev);
      const areAll = idsDelTrimestre.every(id => prevSet.has(id));
      if (areAll) return prev.filter(id => !trimestreIdSet.has(id));
      return [...new Set([...prev, ...idsDelTrimestre])];
    });
  };

  const toggleSelectOne = (itemId, checked) => {
    setSelectedIds(prev => {
      if (checked) return [...new Set([...prev, itemId])];
      return prev.filter(id => id !== itemId);
    });
  };

  const visibleCount = items.filter(i => i.visibleAsesoria).length;
  const canEdit = hasPermission('received_invoices', 'edit');

  // HSL determinista por bitmask de estados (B2)
  const stateBitmask = (item) => {
    let m = 0;
    if (item.visibleAsesoria) m |= 1;
    if (item.quarterClosedAt) m |= 2;
    if (item.quarterOverrideYear && item.quarterOverrideQuarter) m |= 8;
    return m;
  };
  const cardBgFromState = (item) => {
    const m = stateBitmask(item);
    if (m === 0) return '#ffffff';
    const hue = (m * 53 + 211) % 360;
    return `hsl(${hue}, 60%, 95%)`;
  };
  const cardBorderFromState = (item) => {
    const m = stateBitmask(item);
    if (m === 0) return '#e2e8f0';
    const hue = (m * 53 + 211) % 360;
    return `hsl(${hue}, 50%, 75%)`;
  };

  // Columnas de la vista tabla. Un dato por columna de los que muestra la tarjeta
  // y reutilizan los mismos handlers que el pie de la tarjeta (descargar,
  // re-extraer, eliminar). El estado se comunica con los badges de la columna
  // "Estados" (igual criterio que la tabla de Facturas); la visibilidad para la
  // asesoría se cambia pulsando su badge. Acciones y estados van en la segunda
  // fila de cada factura (DataTable segundaFila).
  const columns = [
    { key: 'select', header: '', render: (item) => (
      <input
        type="checkbox"
        checked={selectedSet.has(item.id)}
        onChange={e => toggleSelectOne(item.id, e.target.checked)}
        onClick={e => e.stopPropagation()}
        title="Seleccionar factura"
      />
    ) },
    { key: 'fileName', header: 'Archivo', render: (item) => (
      <div>
        <div>{item.fileName}</div>
        <div className="dt-sub">{formatSize(item.fileSize)} · subida {formatDate(item.createdAt)}</div>
      </div>
    ) },
    { key: 'providerName', header: 'Proveedor', className: 'dt-strong', fixed: true, render: (item) => item.providerName || 'Sin proveedor' },
    { key: 'providerNif', header: 'NIF', className: 'dt-nowrap', render: (item) => item.providerNif || '—' },
    { key: 'invoiceNumber', header: 'Nº factura', render: (item) => item.invoiceNumber || '—' },
    { key: 'date', header: 'Fecha', className: 'dt-nowrap', render: (item) => formatDate(item.date) },
    { key: 'base', header: 'Base', className: 'num', render: (item) => Number(item.amount) > 0 ? formatCurrency((parseFloat(item.amount) || 0) - (parseFloat(item.taxAmount) || 0)) : '—' },
    { key: 'total', header: 'Total c/IVA', className: 'num', render: (item) => Number(item.amount) > 0 ? formatCurrency(item.amount) : <span className="badge badge-gray">Sin importe</span> },
    { key: 'states', header: 'Estados', render: (item) => (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {item.visibleAsesoria ? (
          <span className="badge badge-success" onClick={(e) => { e.stopPropagation(); if (canEdit) handleToggleVisibleAsesoria(item); }} title="Visible para la asesoría · clic para ocultar" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: canEdit ? 'pointer' : 'default' }}><Check size={12} /> Visible asesoría</span>
        ) : (
          <span className="badge badge-gray" onClick={(e) => { e.stopPropagation(); if (canEdit) handleToggleVisibleAsesoria(item); }} title="No visible para la asesoría · clic para hacer visible" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: canEdit ? 'pointer' : 'default' }}><Check size={12} /> No visible</span>
        )}
        {item.quarterClosedAt ? (
          <span className="badge badge-info" title={`Trimestre cerrado el ${formatDateTime(item.quarterClosedAt)}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><CalendarCheck2 size={12} /> Trim. cerrado</span>
        ) : (
          <span className="badge badge-gray" title="Trimestre abierto" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><CalendarCheck2 size={12} /> Trim. abierto</span>
        )}
        {item.isAccounted ? (
          <span className="badge badge-success" onClick={(e) => { e.stopPropagation(); if (canEdit) toggleAccounted(item.id, false); }} title="Contabilizada (confirmada por la asesoría) · clic para desmarcar" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: canEdit ? 'pointer' : 'default' }}><FileCheck size={12} /> Contabilizada</span>
        ) : (
          <span className="badge badge-gray" onClick={(e) => { e.stopPropagation(); if (canEdit) toggleAccounted(item.id, true); }} title="Sin contabilizar · clic para marcar como contabilizada" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: canEdit ? 'pointer' : 'default' }}><FileCheck size={12} /> Sin contabilizar</span>
        )}
        {(item.quarterOverrideYear && item.quarterOverrideQuarter) ? (
          <span className="badge badge-warning" title={`Trimestre forzado a ${item.quarterOverrideYear}-T${item.quarterOverrideQuarter}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><ArrowRightCircle size={12} /> {item.quarterOverrideYear}·T{item.quarterOverrideQuarter}</span>
        ) : (
          <span className="badge badge-gray" title="Sin cambio de trimestre" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><ArrowRightCircle size={12} /> Sin cambio de trimestre</span>
        )}
      </div>
    ) },
    { key: 'actions', header: '', className: 'col-actions', render: (item) => {
      const canDelete = hasPermission('received_invoices', 'delete');
      const showDeleteButton = canDelete;
      return (
        <div className="dt-actions" onClick={e => e.stopPropagation()}>
          <button className="btn-icon" onClick={() => handleViewFile(item)} title="Ver documento"><Eye size={16} /></button>
          <button className="btn-icon" onClick={() => handleDownload(item)} title="Descargar"><Download size={16} /></button>
          {canEdit && (
            <button
              className="btn-icon"
              onClick={() => handleReextractOne(item)}
              disabled={reextractingId !== null}
              title={reextractingId === item.id ? 'Re-extrayendo…' : 'Re-extraer datos con IA'}
              style={reextractingId === item.id ? { opacity: 0.6 } : undefined}
            ><Sparkles size={16} /></button>
          )}
          {canEdit && (
            <button
              className="btn-icon"
              onClick={() => handleRegenerateTemplate(item)}
              disabled={regeneratingId !== null}
              title={regeneratingId === item.id
                ? 'Regenerando plantilla…'
                : item.templateRegeneratedAt
                  ? `Regenerar plantilla del proveedor (última vez: ${formatDateTime(item.templateRegeneratedAt)})`
                  : 'Regenerar plantilla del proveedor: los datos de esta factura han entrado mal'}
              style={regeneratingId === item.id ? { opacity: 0.6 } : undefined}
            ><Wand2 size={16} /></button>
          )}
          {showDeleteButton && (
            <button
              className="btn-icon"
              onClick={() => canDelete && handleDelete(item)}
              disabled={!canDelete}
              title={canDelete ? 'Eliminar' : 'Sin permiso de eliminación'}
              style={canDelete ? { color: '#ef4444' } : { opacity: 0.55, cursor: 'not-allowed' }}
            ><Trash2 size={16} /></button>
          )}
        </div>
      );
    } },
  ];

  const fields = fl.fields(columns);

  // Menú «Acciones», junto a las casillas de marcar todas: lo que se puede
  // hacer a la vez con las marcadas. Son los botones que antes iban sueltos en
  // esa fila (solo salían con algo marcado). Cada rótulo dice la acción con el
  // mismo nombre que su confirmación, nunca el estado en que quedan.
  const accionesSeleccion = [
    { key: 'asesoria-si', label: `Marcar como visibles para la asesoría (${selectedNotVisibleCount})`, icon: Check, disabled: selectedNotVisibleCount === 0, onClick: () => bulkVisibleAsesoria(true), title: 'Marcar las seleccionadas como visibles para la asesoría' },
    { key: 'asesoria-no', label: `Ocultar a la asesoría (${selectedVisibleCount})`, icon: EyeOff, disabled: selectedVisibleCount === 0, onClick: () => bulkVisibleAsesoria(false), title: 'Ocultar las seleccionadas a la asesoría' },
    { key: 'contabilizadas', label: `Marcar como contabilizadas (${selectedNotAccountedCount})`, icon: FileCheck, disabled: selectedNotAccountedCount === 0, onClick: () => bulkMarkAccounted(true), title: 'Marcar las seleccionadas como contabilizadas (cuando la asesoría lo confirma). Se refleja en el portal de asesoría.' },
    { key: 'quitar-contab', label: `Quitar la marca de contabilizada (${selectedAccountedCount})`, icon: XIcon, disabled: selectedAccountedCount === 0, onClick: () => bulkMarkAccounted(false), title: 'Quitar la marca de contabilizada en las seleccionadas' },
    { key: 'cerrar', label: `Cerrar trimestre (${selectedNotClosedCount})`, icon: CalendarCheck2, disabled: selectedNotClosedCount === 0, onClick: bulkQuarterClose, title: 'Cerrar trimestre en las seleccionadas' },
    { key: 'trim-anterior', label: 'Mover al trimestre anterior', icon: ArrowLeftCircle, disabled: selectedShownCount === 0, onClick: () => bulkQuarterShift('prev'), title: 'Trasladar las seleccionadas al trimestre anterior' },
    { key: 'trim-siguiente', label: 'Mover al trimestre siguiente', icon: ArrowRightCircle, disabled: selectedShownCount === 0, onClick: () => bulkQuarterShift('next'), title: 'Trasladar las seleccionadas al trimestre siguiente' },
    { key: 'quitar-override', label: 'Quitar el cambio de trimestre', icon: XIcon, disabled: selectedShownCount === 0, onClick: () => bulkQuarterShift('clear'), title: 'Quitar el cambio de trimestre (volver a fecha real)' },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Facturas recibidas</h1>
          <p className="page-subtitle">
            Mostrando {quarter === 'all' ? 'año' : `T${quarter}`} {year} · {sortedFiltered.length} archivos · {items.length} en total · {visibleCount} visibles asesoría{selectedShownCount > 0 ? ` · ${selectedShownCount} seleccionada${selectedShownCount === 1 ? '' : 's'}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {canEdit && (
            <button
              className="btn btn-outline"
              onClick={handleReextract}
              disabled={reextracting || selectedIds.length === 0}
              title={selectedIds.length === 0
                ? 'Marca facturas con el checkbox para reprocesar su OCR.'
                : `Vuelve a procesar el OCR de las ${selectedIds.length} factura${selectedIds.length === 1 ? '' : 's'} marcada${selectedIds.length === 1 ? '' : 's'} y crea proveedores nuevos por NIF.`}
            >
              <Search size={18} /> {reextracting
                ? 'Reprocesando…'
                : `Re-extraer datos${selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}`}
            </button>
          )}
          <button
            className="btn btn-outline"
            onClick={exportCsv}
            title="Exporta a CSV el listado filtrado actual."
            disabled={sortedFiltered.length === 0}
          >
            <FileSpreadsheet size={18} /> Exportar CSV
          </button>
        </div>
      </div>

      {/* Zona de subida */}
      {hasPermission('received_invoices', 'create') && (
        <div
          style={{
            border: `2px dashed ${dragOver ? 'var(--primary)' : 'var(--gray-300)'}`,
            borderRadius: 8,
            textAlign: 'center',
            padding: uploading ? '16px' : '32px 16px',
            cursor: uploading ? 'default' : 'pointer',
            marginBottom: 20,
            background: dragOver ? 'var(--gray-100)' : 'transparent',
            transition: 'all 0.2s'
          }}
          onDragOver={e => { e.preventDefault(); if (!uploading) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); if (!uploading) handleFiles(e.dataTransfer.files); }}
          onClick={() => !uploading && fileInputRef.current?.click()}
        >
          {uploading && uploadProgress ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{
                  width: 22, height: 22, border: '3px solid var(--gray-200)',
                  borderTop: '3px solid var(--primary)', borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite'
                }} />
                <span style={{ fontWeight: 600, color: 'var(--primary)', fontSize: '0.95rem' }}>
                  {uploadProgress.current} / {uploadProgress.total}
                </span>
              </div>
              <div style={{
                background: 'var(--gray-200)', borderRadius: 4, height: 6,
                maxWidth: 400, margin: '0 auto 8px'
              }}>
                <div style={{
                  background: 'var(--primary)', borderRadius: 4, height: '100%',
                  width: `${(uploadProgress.current / uploadProgress.total) * 100}%`,
                  transition: 'width 0.3s ease'
                }} />
              </div>
              <p style={{ color: 'var(--gray-500)', margin: 0, fontSize: '0.85rem' }}>
                {uploadProgress.fileName}
              </p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : (
            <>
              <Upload size={40} style={{ color: 'var(--gray-400)', marginBottom: 8 }} />
              <p style={{ color: 'var(--gray-600)', margin: '8px 0 0', fontSize: '0.95rem' }}>
                Arrastra aquí tus facturas (PDF, JPG, PNG) o haz clic para seleccionar
              </p>
              <p style={{ color: 'var(--gray-400)', margin: '4px 0 0', fontSize: '0.8rem' }}>
                Los datos de los PDFs se detectan automáticamente
              </p>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            multiple
            style={{ display: 'none' }}
            onChange={e => handleFiles(e.target.files)}
          />
        </div>
      )}

      {/* Pestañas de trimestre */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              type="button"
              className="btn-icon"
              onClick={() => setYear(year - 1)}
              disabled={year <= minYear}
              title="Año anterior"
              style={{ padding: '6px 10px' }}
            >◀</button>
            <span style={{ fontWeight: 700, fontSize: 16, minWidth: 56, textAlign: 'center' }}>{year}</span>
            <button
              type="button"
              className="btn-icon"
              onClick={() => setYear(year + 1)}
              disabled={year >= maxYear}
              title="Año siguiente"
              style={{ padding: '6px 10px' }}
            >▶</button>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[1, 2, 3, 4].map(q => {
              const isActive = q === quarter;
              const count = quarterCounts[q - 1];
              const isCurrentRealQuarter = year === currentYear && q === currentQuarter;
              return (
                <button
                  key={q}
                  type="button"
                  onClick={() => setQuarter(q)}
                  className={isActive ? 'btn btn-primary' : 'btn btn-outline'}
                  style={{ padding: '6px 14px' }}
                  title={isCurrentRealQuarter ? 'Trimestre actual' : `Trimestre ${q}`}
                >
                  T{q}{isCurrentRealQuarter ? ' ·' : ''} <span style={{ opacity: 0.7, marginLeft: 4 }}>({count})</span>
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => setQuarter('all')}
              className={quarter === 'all' ? 'btn btn-primary' : 'btn btn-outline'}
              style={{ padding: '6px 14px' }}
              title="Todo el año"
            >
              Año <span style={{ opacity: 0.7, marginLeft: 4 }}>({quarterCounts.reduce((a, b) => a + b, 0)})</span>
            </button>
            {quarter !== 'all' && closableCount > 0 && canEdit && (
              <button
                type="button"
                onClick={closeCurrentQuarter}
                className="btn"
                style={{ padding: '6px 14px', marginLeft: 12, background: 'var(--warning)', color: '#fff', border: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600 }}
                title={`Cerrar el trimestre T${quarter} de ${year}: marca como cerradas las ${closableCount} factura(s) recibida(s) de ese periodo. El cierre de recibidas no se puede revertir.`}
              >
                <Lock size={14} /> Cerrar T{quarter} ({closableCount})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="card">
        <div className="search-bar">
          {/* Con ancho mínimo, como el buscador de Salientes: si no cabe todo
              en una fila, los desplegables pasan a la siguiente en vez de
              dejar el buscador en nada. */}
          <div className="search-input-wrapper" style={{ flex: '1 1 260px', minWidth: 220, ...(search ? { boxShadow: '0 0 0 2px var(--primary)', borderRadius: 6 } : {}) }}>
            <Search />
            <input
              type="text"
              className="form-input"
              placeholder="Buscar por archivo, proveedor, NIF o nº..."
              value={search}
              onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))}
            />
          </div>
          <select
            className="form-input"
            style={{ width: 200, ...(visibleFilter ? { borderColor: 'var(--primary)', boxShadow: '0 0 0 1px var(--primary)' } : {}) }}
            value={visibleFilter}
            onChange={e => setFilters(prev => ({ ...prev, visibleFilter: e.target.value }))}
            title="Visible para la asesoría"
          >
            <option value="">Visibilidad (todas)</option>
            <option value="si">Visible asesoría</option>
            <option value="no">No visible</option>
          </select>
          <select
            className="form-input"
            style={{ width: 200, ...(closedFilter ? { borderColor: 'var(--primary)', boxShadow: '0 0 0 1px var(--primary)' } : {}) }}
            value={closedFilter}
            onChange={e => setFilters(prev => ({ ...prev, closedFilter: e.target.value }))}
            title="Cierre trimestral"
          >
            <option value="">Cierre (todas)</option>
            <option value="si">Trimestre cerrado</option>
            <option value="no">Trimestre abierto</option>
          </select>
          <select
            className="form-input"
            style={{ width: 240, ...(accountedFilter ? { borderColor: 'var(--primary)', boxShadow: '0 0 0 1px var(--primary)' } : {}) }}
            value={accountedFilter}
            onChange={e => setFilters(prev => ({ ...prev, accountedFilter: e.target.value }))}
            title="Contabilización"
          >
            <option value="">Contabilización (todas)</option>
            <option value="si">Contabilizadas</option>
            <option value="no">Sin contabilizar</option>
          </select>
          <select
            className="form-input"
            style={{ width: 240, ...(supplierFilter ? { borderColor: 'var(--primary)', boxShadow: '0 0 0 1px var(--primary)' } : {}) }}
            value={supplierFilter}
            onChange={e => setFilters(prev => ({ ...prev, supplierFilter: e.target.value }))}
            title="Proveedor"
          >
            <option value="">Todos los proveedores</option>
            {Array.from(new Set(items.map(i => i.providerName).filter(Boolean)))
              .sort((a, b) => a.localeCompare(b))
              .map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
          </select>
          <select
            className="form-input"
            style={{ width: 220 }}
            value={sortKey}
            onChange={e => setFilters(prev => ({ ...prev, sortKey: e.target.value }))}
            title="Orden"
          >
            <option value="date_desc">Fecha (más reciente)</option>
            <option value="date_asc">Fecha (más antigua)</option>
            <option value="provider_asc">Proveedor A → Z</option>
            <option value="provider_desc">Proveedor Z → A</option>
            <option value="amount_desc">Importe (mayor)</option>
            <option value="amount_asc">Importe (menor)</option>
            <option value="invoice_num">Nº factura ↑</option>
            <option value="invoice_num_desc">Nº factura ↓</option>
          </select>
          {activeFilterCount > 0 && (
            <span style={{
              alignSelf: 'center', fontSize: 12, fontWeight: 700, color: 'var(--primary)',
              background: 'rgba(59,130,246,0.10)', padding: '4px 10px', borderRadius: 999,
            }}>Filtros activos: {activeFilterCount}</span>
          )}
          <div style={{ marginLeft: 'auto' }}>
            <VistaControles
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              fields={fields}
              hidden={fl.hidden}
              onToggleField={fl.toggle}
              onReorderFields={fl.reorder}
            />
          </div>
        </div>

        {!loading && filtered.length > 0 && (
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center',
            padding: '10px 14px', margin: '8px 0 12px', background: '#f8fafc',
            border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13,
          }}>
            <span style={{ color: '#64748b', fontWeight: 600 }}>Totales ({filtered.length}):</span>
            <span><span style={{ color: '#64748b' }}>Base imponible</span> <strong style={{ marginLeft: 4 }}>{formatCurrency(totals.base)}</strong></span>
            <span><span style={{ color: '#64748b' }}>IVA</span> <strong style={{ marginLeft: 4 }}>{formatCurrency(totals.iva)}</strong></span>
            <span style={{ marginLeft: 'auto' }}><span style={{ color: '#64748b' }}>Total</span> <strong style={{ marginLeft: 4, fontSize: 15 }}>{formatCurrency(totals.total)}</strong></span>
          </div>
        )}

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)' }}>Cargando...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <FileText size={64} />
            <h3>No hay facturas recibidas</h3>
            <p>Sube la primera factura arrastrándola a la zona de arriba</p>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              {canEdit && (
                <AccionesSeleccion
                  count={selectedShownCount}
                  scope={quarter === 'all' ? `de ${year}` : `del T${quarter} de ${year}`}
                  disabled={bulkRunning}
                  actions={accionesSeleccion}
                />
              )}
              <input
                type="checkbox"
                checked={allFilteredSelected}
                onChange={toggleSelectAllFiltered}
                disabled={selectableFilteredIds.length === 0}
                title="Seleccionar/des-seleccionar todas las visibles"
              />
              <span className="item-card-note">
                Marcar todas las visibles ({selectableFilteredIds.length}){selectedShownCount > 0 ? ` · ${selectedShownCount} seleccionada${selectedShownCount === 1 ? '' : 's'}` : ''}
              </span>
              <input
                type="checkbox"
                checked={allTrimestreSelected}
                onChange={toggleSelectAllTrimestre}
                disabled={idsDelTrimestre.length === 0}
                title="Seleccionar/des-seleccionar TODAS las facturas del trimestre (por su fecha), aparezcan o no en el filtro. Solo selecciona; no cambia estados."
              />
              <span className="item-card-note">
                Marcar todas del {quarter === 'all' ? 'año' : `trimestre T${quarter}`} ({idsDelTrimestre.length})
              </span>
            </div>

            {viewMode === 'table' ? (
              <DataTable
                hiddenKeys={fl.hidden}
                columns={fl.orderColumns(columns)}
                rows={sortedFiltered}
                segundaFila={['actions', 'states']}
                getRowProps={(item) => ({ onClick: () => openDetail(item), style: { cursor: 'pointer' } })}
              />
            ) : (
            <div className="items-grid">
              {sortedFiltered.map(item => {
                const canDelete = hasPermission('received_invoices', 'delete');
                const showDeleteButton = canDelete;
                const cardBg = cardBgFromState(item);
                const cardBorder = cardBorderFromState(item);

                return (
                  <div
                    key={item.id}
                    className="item-card item-card--interactive"
                    style={{ background: cardBg, borderColor: cardBorder }}
                    onClick={() => openDetail(item)}
                  >
                    <div className="item-card-header">
                      <div className="item-card-main">
                        <div className="item-card-check" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedSet.has(item.id)}
                            onChange={e => toggleSelectOne(item.id, e.target.checked)}
                            title="Seleccionar factura"
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="item-card-title" style={{ wordBreak: 'break-word' }}>
                            {item.fileName}
                          </div>
                          <div className="item-card-subtitle">
                            {formatSize(item.fileSize)} · subida {formatDate(item.createdAt)}
                          </div>
                        </div>
                      </div>

                      {Number(item.amount) > 0 ? (
                        <div className="item-card-amount" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                          {!fl.isHidden('base') && (
                          <span title="Base imponible">
                            <span style={{ fontSize: '0.72em', color: '#64748b', fontWeight: 500, marginRight: 4 }}>Base</span>
                            {formatCurrency((parseFloat(item.amount) || 0) - (parseFloat(item.taxAmount) || 0))}
                          </span>
                          )}
                          {!fl.isHidden('total') && (
                          <span style={{ fontSize: '0.72em', color: '#64748b', fontWeight: 500 }} title="Total con IVA">
                            Tot. c/IVA {formatCurrency(item.amount)}
                          </span>
                          )}
                        </div>
                      ) : (
                        <span className="badge badge-gray">Sin importe</span>
                      )}
                    </div>

                    <div className="item-card-body">
                      <div className="item-card-row">
                        <span className="item-label">Proveedor</span>
                        <span style={{ fontWeight: 500 }}>
                          {item.providerName || 'Sin proveedor'}
                        </span>
                      </div>
                      {!fl.isHidden('providerNif') && (
                      <div className="item-card-row" style={fl.cardStyle('providerNif')}>
                        <span className="item-label">NIF</span>
                        <span>{item.providerNif || '—'}</span>
                      </div>
                      )}
                      {!fl.isHidden('invoiceNumber') && (
                      <div className="item-card-row" style={fl.cardStyle('invoiceNumber')}>
                        <span className="item-label">N.º factura</span>
                        <span>{item.invoiceNumber || '—'}</span>
                      </div>
                      )}
                      {!fl.isHidden('date') && (
                      <div className="item-card-row" style={fl.cardStyle('date')}>
                        <span className="item-label">Fecha</span>
                        <span>{formatDate(item.date)}</span>
                      </div>
                      )}

                      {!fl.isHidden('states') && (
                      <div className="item-card-row" style={{ ...fl.cardStyle('states'), alignItems: 'flex-start', flexDirection: 'column', gap: 6 }}>
                        <span className="item-label" style={{ alignSelf: 'flex-start' }}>Estados</span>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                          gap: 6,
                          width: '100%',
                        }}>
                          {/* Visible asesoría */}
                          {item.visibleAsesoria ? (
                            <span
                              className="badge badge-success"
                              onClick={(e) => { e.stopPropagation(); if (canEdit) handleToggleVisibleAsesoria(item); }}
                              title="Visible para la asesoría · clic para ocultar"
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, cursor: canEdit ? 'pointer' : 'default', width: '100%' }}
                            >
                              <Check size={12} /> Visible asesoría
                            </span>
                          ) : (
                            <span
                              className="badge badge-gray"
                              onClick={(e) => { e.stopPropagation(); if (canEdit) handleToggleVisibleAsesoria(item); }}
                              title="No visible para la asesoría · clic para hacer visible"
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, cursor: canEdit ? 'pointer' : 'default', width: '100%' }}
                            >
                              <Check size={12} /> No visible
                            </span>
                          )}

                          {/* Trimestre cerrado */}
                          {item.quarterClosedAt ? (
                            <span
                              className="badge badge-info"
                              title={`Trimestre cerrado el ${formatDateTime(item.quarterClosedAt)}`}
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, width: '100%' }}
                            >
                              <CalendarCheck2 size={12} /> Trim. cerrado
                            </span>
                          ) : (
                            <span
                              className="badge badge-gray"
                              title="Trimestre abierto"
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, width: '100%' }}
                            >
                              <CalendarCheck2 size={12} /> Trim. abierto
                            </span>
                          )}

                          {/* Contabilizada — la confirma la asesoría; clic para marcar/desmarcar.
                              Se refleja en el portal de asesoría. */}
                          {item.isAccounted ? (
                            <span
                              className="badge badge-success"
                              onClick={(e) => { e.stopPropagation(); if (canEdit) toggleAccounted(item.id, false); }}
                              title="Contabilizada (confirmada por la asesoría) · clic para desmarcar"
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, cursor: canEdit ? 'pointer' : 'default', width: '100%' }}
                            >
                              <FileCheck size={12} /> Contabilizada
                            </span>
                          ) : (
                            <span
                              className="badge badge-gray"
                              onClick={(e) => { e.stopPropagation(); if (canEdit) toggleAccounted(item.id, true); }}
                              title="Sin contabilizar · clic para marcar como contabilizada"
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, cursor: canEdit ? 'pointer' : 'default', width: '100%' }}
                            >
                              <FileCheck size={12} /> Sin contabilizar
                            </span>
                          )}

                          {/* Override de trimestre */}
                          {(item.quarterOverrideYear && item.quarterOverrideQuarter) ? (
                            <span
                              className="badge badge-warning"
                              title={`Trimestre forzado a ${item.quarterOverrideYear}-T${item.quarterOverrideQuarter}`}
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, width: '100%' }}
                            >
                              <ArrowRightCircle size={12} /> {item.quarterOverrideYear}·T{item.quarterOverrideQuarter}
                            </span>
                          ) : (
                            <span
                              className="badge badge-gray"
                              title="Sin cambio de trimestre"
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, width: '100%' }}
                            >
                              <ArrowRightCircle size={12} /> Sin cambio de trimestre
                            </span>
                          )}
                        </div>
                      </div>
                      )}
                    </div>

                    <div className="item-card-footer">
                      <span className="item-card-date">Abrir para editar y revisar OCR</span>
                      <div className="item-card-actions" onClick={e => e.stopPropagation()}>
                        <button className="btn btn-sm btn-outline" title="Ver documento" onClick={() => handleViewFile(item)}>
                          <Eye size={14} />
                        </button>
                        <button className="btn btn-sm btn-outline" title="Descargar" onClick={() => handleDownload(item)}>
                          <Download size={14} />
                        </button>
                        {canEdit && (
                          <button
                            className="btn btn-sm btn-outline"
                            title={reextractingId === item.id ? 'Re-extrayendo…' : 'Re-extraer datos con IA'}
                            onClick={() => handleReextractOne(item)}
                            disabled={reextractingId !== null}
                            style={reextractingId === item.id
                              ? { opacity: 0.6 }
                              : undefined}
                          >
                            <Sparkles size={14} />
                          </button>
                        )}
                        {canEdit && (
                          <button
                            className="btn btn-sm btn-outline"
                            title={regeneratingId === item.id
                              ? 'Regenerando plantilla…'
                              : item.templateRegeneratedAt
                                ? `Regenerar plantilla del proveedor (última vez: ${formatDateTime(item.templateRegeneratedAt)})`
                                : 'Regenerar plantilla del proveedor: los datos de esta factura han entrado mal'}
                            onClick={() => handleRegenerateTemplate(item)}
                            disabled={regeneratingId !== null}
                            style={regeneratingId === item.id
                              ? { opacity: 0.6 }
                              : undefined}
                          >
                            <Wand2 size={14} />
                          </button>
                        )}
                        {showDeleteButton && (
                          <button
                            className="btn btn-sm btn-danger"
                            title={canDelete ? 'Eliminar' : 'Sin permiso de eliminación'}
                            onClick={() => canDelete && handleDelete(item)}
                            disabled={!canDelete}
                            style={!canDelete ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            )}
          </div>
        )}
      </div>
      {cierre && (
        <CierreTrimestreModal
          grupos={cierre.grupos}
          documento={{ uno: 'factura', varios: 'facturas' }}
          nota="El cierre de las facturas recibidas no se puede deshacer."
          ocupado={bulkRunning}
          onCancel={() => setCierre(null)}
          onConfirm={confirmarCierre}
        />
      )}

      {/* Modal de detalle / edición */}
      {detailItem && editForm && (
        <div className="modal-overlay" onClick={closeDetail}>
          <div className="app-modal app-modal--wide app-modal--form" onClick={e => e.stopPropagation()}>
            <button className="app-modal-close" onClick={closeDetail} aria-label="Cerrar">
              <XIcon size={18} />
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <FileText size={24} style={{ color: 'var(--primary)', flexShrink: 0 }} />
              <div style={{ minWidth: 0 }}>
                <h3 style={{ margin: 0, fontSize: '1rem', wordBreak: 'break-word' }}>{detailItem.fileName}</h3>
                <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                  {formatSize(detailItem.fileSize)} · {formatDate(detailItem.createdAt)}
                </span>
              </div>
            </div>

            {detailItem.quarterClosedAt && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16, padding: '6px 10px', background: '#eef2ff', borderRadius: 6 }}>
                <CalendarCheck2 size={14} style={{ color: '#4338ca' }} />
                <span style={{ fontSize: '0.8rem', color: '#4338ca', fontWeight: 500 }}>
                  Incluida en cierre trimestral: {formatDateTime(detailItem.quarterClosedAt)}
                </span>
              </div>
            )}

            {detailItem.visibleAsesoria ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16, padding: '6px 10px', background: '#f0fdf4', borderRadius: 6 }}>
                <Check size={14} style={{ color: 'var(--asesoria)' }} />
                <span style={{ fontSize: '0.8rem', color: '#15803d', fontWeight: 500 }}>
                  Visible para la asesoría
                </span>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16, padding: '6px 10px', background: '#f3f4f6', borderRadius: 6 }}>
                <EyeOff size={14} style={{ color: '#6b7280' }} />
                <span style={{ fontSize: '0.8rem', color: '#374151', fontWeight: 500 }}>
                  No visible para la asesoría
                </span>
              </div>
            )}

            {(detailItem.quarterOverrideYear && detailItem.quarterOverrideQuarter) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16, padding: '6px 10px', background: '#fff7ed', borderRadius: 6 }}>
                <ArrowRightCircle size={14} style={{ color: '#c2410c' }} />
                <span style={{ fontSize: '0.8rem', color: '#9a3412', fontWeight: 500 }}>
                  Trimestre forzado: {detailItem.quarterOverrideYear} · T{detailItem.quarterOverrideQuarter}
                </span>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>Proveedor</label>
                <input className="form-input" value={editForm.providerName}
                  onChange={e => setEditForm(f => ({ ...f, providerName: e.target.value }))}
                  placeholder="Nombre del proveedor" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>NIF/CIF</label>
                <input className="form-input" value={editForm.providerNif}
                  onChange={e => setEditForm(f => ({ ...f, providerNif: e.target.value }))}
                  placeholder="B12345678" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>Nº Factura</label>
                <input className="form-input" value={editForm.invoiceNumber}
                  onChange={e => setEditForm(f => ({ ...f, invoiceNumber: e.target.value }))}
                  placeholder="F-2026/001" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>Fecha factura</label>
                <input className="form-input" type="date" value={editForm.date}
                  onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>Importe total (€)</label>
                <DecimalInput value={editForm.amount}
                  onValueChange={value => setEditForm(f => ({ ...f, amount: value }))}
                  placeholder="0.00" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>IVA (€)</label>
                <DecimalInput value={editForm.taxAmount}
                  onValueChange={value => setEditForm(f => ({ ...f, taxAmount: value }))}
                  placeholder="0.00" />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>Notas</label>
                <textarea className="form-input" rows={2} value={editForm.notes}
                  onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Observaciones..." />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20, gap: 8 }}>
              <button className="btn btn-outline" onClick={() => handleDownload(detailItem)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Download size={14} /> Descargar archivo
              </button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-outline" onClick={closeDetail}>Cerrar</button>
                {hasPermission('received_invoices', 'edit') && (
                  <button className="btn btn-primary" onClick={handleSaveDetail} disabled={saving}
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Save size={14} /> {saving ? 'Guardando...' : 'Guardar'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceivedInvoices;
